<template>
  <div class="recommendations-section">
    <h3 class="section-title">
      <i class="fas fa-lightbulb"></i> Recomendações de Políticas Públicas
    </h3>
    
    <div class="policies-grid">
      <div 
        v-for="(policy, index) in policies" 
        :key="'policy-'+index"
        class="policy-card"
      >
        <div class="policy-icon" :class="policy.type">
          <i :class="'fas ' + policy.icon"></i>
        </div>
        <div class="policy-content">
          <h4 class="policy-title">{{ policy.title }}</h4>
          <p class="policy-description">{{ policy.description }}</p>
          <div class="policy-timeline">
            <div class="timeline-label">Prazo de Implementação:</div>
            <div class="timeline-value">{{ policy.timeline }}</div>
          </div>
          <div class="policy-impact">
            <div class="impact-label">Impacto Estimado:</div>
            <div class="impact-scale">
              <div 
                class="impact-bar" 
                :style="{ width: (policy.impact * 20) + '%' }"
                :class="getImpactClass(policy.impact)"
              ></div>
            </div>
            <div class="impact-value">{{ policy.impact }}/5</div>
          </div>
          <div class="policy-tags">
            <div class="tag" v-for="(tag, tagIndex) in policy.tags" :key="'tag-'+index+'-'+tagIndex">
              {{ tag }}
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <div class="strategic-action-plan">
      <h4 class="strategic-title">
        <i class="fas fa-tasks"></i> Plano de Ação Estratégico - {{ categoryName }} ({{ monthName }})
      </h4>
      <div class="action-steps">
        <div class="action-step" v-for="(step, index) in actionSteps" :key="'step-'+index">
          <div class="step-number">{{ index + 1 }}</div>
          <div class="step-content">
            <h5 class="step-title">{{ step.title }}</h5>
            <p class="step-description">{{ step.description }}</p>
            <div class="step-details">
              <div class="detail-item">
                <i class="fas fa-calendar-alt"></i>
                <span>{{ step.timeline }}</span>
              </div>
              <div class="detail-item">
                <i class="fas fa-user-tie"></i>
                <span>{{ step.responsible }}</span>
              </div>
              <div class="detail-item" :class="'priority-'+step.priority">
                <i class="fas fa-flag"></i>
                <span>{{ getPriorityLabel(step.priority) }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  props: {
    categoryData: Object,
    categoryName: String,
    month: Number,
    monthName: String
  },
  data() {
    return {
      policies: [],
      actionSteps: []
    }
  },
  mounted() {
    this.generatePolicyRecommendations()
    this.generateActionPlan()
  },
  methods: {
    generatePolicyRecommendations() {
      // Em uma implementação real, as recomendações seriam geradas com base em análises
      // Aqui estamos usando dados simulados para demonstração
      
      // Políticas específicas para cada setor
      const sectorPolicies = {
        'Gastronomia': [
          {
            title: 'Programa de Certificação Gastronômica',
            description: 'Implementar um sistema de certificação para estabelecimentos gastronômicos, com critérios de qualidade, higiene e atendimento.',
            type: 'certification',
            icon: 'fa-certificate',
            timeline: 'Médio Prazo (6-12 meses)',
            impact: 4,
            tags: ['Qualidade', 'Padronização', 'Turismo']
          },
          {
            title: 'Festival Gastronômico Sazonal',
            description: 'Organizar festivais gastronômicos sazonais para promover a culinária local e atrair visitantes em períodos de baixa temporada.',
            type: 'promotion',
            icon: 'fa-utensils',
            timeline: 'Curto Prazo (3-6 meses)',
            impact: 3,
            tags: ['Turismo', 'Sazonalidade', 'Promoção']
          },
          {
            title: 'Incentivos para Insumos Locais',
            description: 'Criar incentivos fiscais para estabelecimentos que utilizem produtos locais, fortalecendo a cadeia produtiva regional.',
            type: 'incentive',
            icon: 'fa-leaf',
            timeline: 'Médio Prazo (6-12 meses)',
            impact: 4,
            tags: ['Sustentabilidade', 'Economia Local', 'Qualidade']
          }
        ],
        'Hospedagem': [
          {
            title: 'Programa de Qualificação Hoteleira',
            description: 'Implementar um programa de capacitação contínua para profissionais do setor hoteleiro, com foco em hospitalidade e atendimento.',
            type: 'education',
            icon: 'fa-graduation-cap',
            timeline: 'Médio Prazo (6-12 meses)',
            impact: 5,
            tags: ['Capacitação', 'Qualidade', 'Atendimento']
          },
          {
            title: 'Certificação de Sustentabilidade',
            description: 'Criar um sistema de certificação para meios de hospedagem sustentáveis, promovendo práticas ecoeficientes.',
            type: 'certification',
            icon: 'fa-leaf',
            timeline: 'Longo Prazo (12-24 meses)',
            impact: 4,
            tags: ['Sustentabilidade', 'Meio Ambiente', 'Inovação']
          },
          {
            title: 'Regulamentação de Aluguéis por Temporada',
            description: 'Estabelecer marco regulatório para aluguéis por temporada e plataformas digitais, garantindo segurança e qualidade.',
            type: 'regulation',
            icon: 'fa-building',
            timeline: 'Médio Prazo (6-12 meses)',
            impact: 3,
            tags: ['Regulação', 'Segurança', 'Tecnologia']
          }
        ],
        'Atrações Turísticas': [
          {
            title: 'Sistema Integrado de Bilheteria Digital',
            description: 'Implementar um sistema unificado de bilheteria digital para atrações turísticas, facilitando o acesso dos visitantes.',
            type: 'technology',
            icon: 'fa-ticket-alt',
            timeline: 'Médio Prazo (6-12 meses)',
            impact: 4,
            tags: ['Digitalização', 'Experiência', 'Acesso']
          },
          {
            title: 'Fundo de Conservação do Patrimônio',
            description: 'Criar um fundo para conservação e restauração de atrações turísticas e patrimônios históricos e culturais.',
            type: 'preservation',
            icon: 'fa-landmark',
            timeline: 'Longo Prazo (12-24 meses)',
            impact: 5,
            tags: ['Preservação', 'Cultura', 'Sustentabilidade']
          },
          {
            title: 'Programação Cultural Permanente',
            description: 'Estabelecer uma programação cultural permanente em pontos turísticos, com atrações em diferentes períodos do ano.',
            type: 'promotion',
            icon: 'fa-theater-masks',
            timeline: 'Curto Prazo (3-6 meses)',
            impact: 3,
            tags: ['Cultura', 'Sazonalidade', 'Engajamento']
          }
        ],
        'Transporte': [
          {
            title: 'Sistema Integrado de Transporte Turístico',
            description: 'Implementar um sistema integrado de transporte específico para destinos turísticos, com rotas e horários otimizados.',
            type: 'infrastructure',
            icon: 'fa-bus',
            timeline: 'Longo Prazo (12-24 meses)',
            impact: 5,
            tags: ['Mobilidade', 'Integração', 'Experiência']
          },
          {
            title: 'Aplicativo de Mobilidade Turística',
            description: 'Desenvolver um aplicativo que integre informações sobre transporte público, privado e compartilhado para turistas.',
            type: 'technology',
            icon: 'fa-mobile-alt',
            timeline: 'Médio Prazo (6-12 meses)',
            impact: 4,
            tags: ['Digitalização', 'Informação', 'Experiência']
          },
          {
            title: 'Incentivo a Transportes Sustentáveis',
            description: 'Criar incentivos para meios de transporte sustentáveis e de baixa emissão em áreas turísticas, como ciclovias e veículos elétricos.',
            type: 'incentive',
            icon: 'fa-bicycle',
            timeline: 'Médio Prazo (6-12 meses)',
            impact: 3,
            tags: ['Sustentabilidade', 'Meio Ambiente', 'Mobilidade']
          }
        ],
        'Vida Noturna': [
          {
            title: 'Programa "Noite Segura"',
            description: 'Implementar um programa de segurança integrada em áreas de vida noturna, com policiamento, iluminação e monitoramento.',
            type: 'security',
            icon: 'fa-shield-alt',
            timeline: 'Curto Prazo (3-6 meses)',
            impact: 5,
            tags: ['Segurança', 'Experiência', 'Prevenção']
          },
          {
            title: 'Regulamentação de Ruído',
            description: 'Estabelecer regulamentação clara sobre níveis de ruído em estabelecimentos noturnos, equilibrando turismo e bem-estar local.',
            type: 'regulation',
            icon: 'fa-volume-up',
            timeline: 'Curto Prazo (3-6 meses)',
            impact: 3,
            tags: ['Regulação', 'Convivência', 'Sustentabilidade']
          },
          {
            title: 'Festivais Noturnos Temáticos',
            description: 'Organizar festivais noturnos temáticos em diferentes épocas do ano, diversificando a oferta de entretenimento.',
            type: 'promotion',
            icon: 'fa-music',
            timeline: 'Médio Prazo (6-12 meses)',
            impact: 4,
            tags: ['Cultura', 'Sazonalidade', 'Diversidade']
          }
        ],
        'Comércio': [
          {
            title: 'Programa "Comércio Amigo do Turista"',
            description: 'Implementar programa de certificação para estabelecimentos comerciais que ofereçam serviços diferenciados para turistas.',
            type: 'certification',
            icon: 'fa-shopping-bag',
            timeline: 'Médio Prazo (6-12 meses)',
            impact: 4,
            tags: ['Qualidade', 'Experiência', 'Atendimento']
          },
          {
            title: 'Marketplace de Produtos Locais',
            description: 'Desenvolver plataforma digital para promoção e comercialização de produtos locais, acessível a turistas antes e durante a viagem.',
            type: 'technology',
            icon: 'fa-store',
            timeline: 'Médio Prazo (6-12 meses)',
            impact: 3,
            tags: ['Digitalização', 'Economia Local', 'Promoção']
          },
          {
            title: 'Calendário de Horários Estendidos',
            description: 'Estabelecer calendário anual de horários estendidos para o comércio em períodos estratégicos de fluxo turístico.',
            type: 'regulation',
            icon: 'fa-clock',
            timeline: 'Curto Prazo (3-6 meses)',
            impact: 3,
            tags: ['Regulação', 'Experiência', 'Economia']
          }
        ]
      }
      
      // Políticas gerais aplicáveis a todos os setores
      const generalPolicies = [
        {
          title: 'Sistema Integrado de Monitoramento',
          description: 'Implementar sistema de monitoramento contínuo da qualidade de serviços turísticos, com métricas e relatórios periódicos.',
          type: 'monitoring',
          icon: 'fa-chart-line',
          timeline: 'Médio Prazo (6-12 meses)',
          impact: 4,
          tags: ['Dados', 'Qualidade', 'Gestão']
        },
        {
          title: 'Capacitação em Idiomas',
          description: 'Oferecer programas de capacitação em idiomas estrangeiros para profissionais do turismo, melhorando a comunicação com visitantes.',
          type: 'education',
          icon: 'fa-language',
          timeline: 'Longo Prazo (12-24 meses)',
          impact: 4,
          tags: ['Capacitação', 'Comunicação', 'Globalização']
        }
      ]
      
      // Selecionar políticas específicas do setor ou usar políticas gerais
      let specificPolicies = sectorPolicies[this.categoryName] || []
      
      // Combinar políticas específicas e gerais
      this.policies = [...specificPolicies, ...generalPolicies]
      
      // Limitar a 4 políticas no total (priorizando as de maior impacto)
      this.policies.sort((a, b) => b.impact - a.impact)
      this.policies = this.policies.slice(0, 4)
    },
    
    generateActionPlan() {
      // Gerar um plano de ação estratégico com base nas políticas recomendadas
      const steps = []
      
      // Adicionar diagnóstico como primeiro passo
      steps.push({
        title: 'Diagnóstico Detalhado',
        description: `Realizar um diagnóstico detalhado do setor de ${this.categoryName}, mapeando estabelecimentos, avaliações e áreas críticas.`,
        timeline: '1-2 meses',
        responsible: 'Secretaria de Turismo',
        priority: 'high'
      })
      
      // Adicionar passos com base nas políticas recomendadas
      this.policies.forEach((policy, index) => {
        steps.push({
          title: `Implementação: ${policy.title}`,
          description: policy.description,
          timeline: policy.timeline.replace('Prazo ', ''),
          responsible: this.getResponsibleDepartment(policy.type),
          priority: index < 2 ? 'high' : (index === 2 ? 'medium' : 'low')
        })
      })
      
      // Adicionar monitoramento como último passo
      steps.push({
        title: 'Monitoramento e Avaliação',
        description: 'Estabelecer sistema de monitoramento contínuo para avaliar a eficácia das ações implementadas e realizar ajustes necessários.',
        timeline: 'Contínuo',
        responsible: 'Comitê Gestor',
        priority: 'medium'
      })
      
      this.actionSteps = steps
    },
    
    getResponsibleDepartment(policyType) {
      // Determinar o departamento responsável com base no tipo de política
      switch (policyType) {
        case 'education':
        case 'certification':
          return 'Secretaria de Turismo'
        case 'promotion':
        case 'marketing':
          return 'Secretaria de Comunicação'
        case 'technology':
          return 'Secretaria de Tecnologia'
        case 'regulation':
          return 'Departamento Jurídico'
        case 'incentive':
        case 'financial':
          return 'Secretaria de Desenvolvimento'
        case 'infrastructure':
          return 'Secretaria de Obras'
        case 'security':
          return 'Secretaria de Segurança'
        case 'preservation':
          return 'Secretaria de Cultura'
        default:
          return 'Secretaria de Turismo'
      }
    },
    
    getImpactClass(impact) {
      if (impact >= 4) return 'high'
      if (impact >= 3) return 'medium'
      return 'low'
    },
    
    getPriorityLabel(priority) {
      switch (priority) {
        case 'high': return 'Alta Prioridade'
        case 'medium': return 'Média Prioridade'
        default: return 'Baixa Prioridade'
      }
    }
  }
}
</script>

<style scoped>
.recommendations-section {
  margin-bottom: 30px;
}

.section-title {
  font-size: 20px;
  color: #0f172a;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.policies-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-bottom: 30px;
}

.policy-card {
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  display: flex;
  transition: transform 0.2s;
  border: 1px solid #e5e7eb;
}

.policy-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 15px rgba(0, 0, 0, 0.05);
}

.policy-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 60px;
  background: #f8fafc;
  font-size: 24px;
  color: #64748b;
}

.policy-icon.certification {
  background: #e0f2fe;
  color: #0284c7;
}

.policy-icon.education {
  background: #ede9fe;
  color: #7c3aed;
}

.policy-icon.promotion {
  background: #ffedd5;
  color: #ea580c;
}

.policy-icon.incentive {
  background: #ecfccb;
  color: #65a30d;
}

.policy-icon.technology {
  background: #dbeafe;
  color: #2563eb;
}

.policy-icon.regulation {
  background: #fef9c3;
  color: #ca8a04;
}

.policy-icon.monitoring {
  background: #dcfce7;
  color: #16a34a;
}

.policy-icon.security {
  background: #fee2e2;
  color: #b91c1c;
}

.policy-icon.preservation {
  background: #f1f5f9;
  color: #334155;
}

.policy-icon.infrastructure {
  background: #f5f3ff;
  color: #6d28d9;
}

.policy-content {
  flex: 1;
  padding: 20px;
}

.policy-title {
  font-size: 16px;
  font-weight: 600;
  color: #0f172a;
  margin-top: 0;
  margin-bottom: 10px;
}

.policy-description {
  font-size: 14px;
  color: #475569;
  line-height: 1.5;
  margin-bottom: 15px;
}

.policy-timeline {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.timeline-label {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  margin-right: 10px;
  min-width: 150px;
}

.timeline-value {
  font-size: 13px;
  color: #0f172a;
}

.policy-impact {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  gap: 10px;
}

.impact-label {
  font-size: 13px;
  font-weight: 600;
  color: #64748b;
  min-width: 120px;
}

.impact-scale {
  flex: 1;
  height: 8px;
  background: #e2e8f0;
  border-radius: 4px;
  overflow: hidden;
}

.impact-bar {
  height: 100%;
  border-radius: 4px;
}

.impact-bar.high {
  background: #22c55e;
}

.impact-bar.medium {
  background: #f59e0b;
}

.impact-bar.low {
  background: #64748b;
}

.impact-value {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
  min-width: 40px;
}

.policy-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.tag {
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  background: #e2e8f0;
  padding: 3px 8px;
  border-radius: 4px;
}

.strategic-action-plan {
  background: white;
  border-radius: 10px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.strategic-title {
  font-size: 16px;
  font-weight: 600;
  padding: 15px 20px;
  margin: 0;
  background: #f8fafc;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  gap: 8px;
}

.action-steps {
  padding: 20px;
}

.action-step {
  display: flex;
  margin-bottom: 20px;
  position: relative;
}

.action-step:last-child {
  margin-bottom: 0;
}

.action-step:not(:last-child)::after {
  content: '';
  position: absolute;
  top: 40px;
  left: 15px;
  width: 2px;
  height: calc(100% - 15px);
  background: #e2e8f0;
}

.step-number {
  width: 30px;
  height: 30px;
  background: #324DDD;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  margin-right: 15px;
  z-index: 1;
}

.step-content {
  flex: 1;
  background: #f8fafc;
  border-radius: 8px;
  padding: 15px;
}

.step-title {
  font-size: 15px;
  font-weight: 600;
  margin: 0 0 10px;
  color: #0f172a;
}

.step-description {
  font-size: 14px;
  color: #475569;
  line-height: 1.5;
  margin-bottom: 12px;
}

.step-details {
  display: flex;
  flex-wrap: wrap;
  gap: 15px;
}

.detail-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 13px;
  padding: 3px 8px;
  background: #e2e8f0;
  color: #475569;
  border-radius: 4px;
}

.detail-item i {
  font-size: 12px;
}

.detail-item.priority-high {
  background: #dcfce7;
  color: #15803d;
}

.detail-item.priority-medium {
  background: #fef3c7;
  color: #b45309;
}

.detail-item.priority-low {
  background: #e2e8f0;
  color: #475569;
}

@media (max-width: 992px) {
  .policies-grid {
    grid-template-columns: 1fr;
  }
}
</style> 